import { EmailTemplate, Invitation, MessageKey, OrganizerRole, ParticipantState, peaksFor, Preference } from '../models/invitation.model';
import { ORGS_SEED } from '../../../service/data/orgs.seed';

/**
 * Alias, not a copy. The organization list moved to `service/data/orgs.seed.ts` when inventory
 * needed the same one — two arrays would let the app show two different sets of organizations.
 * Kept under the old name for this seed's `rname()` lookups and the existing specs.
 */
export const ROSTERS_SEED = ORGS_SEED;

export const DEFAULT_EMAIL = {
  subject: 'TO DO: Take FourSight Assessment',
  body:
`Dear Participant,

In our upcoming session, we will be reviewing the results of the "FourSight Thinking Profile." FourSight is a multi-item assessment that reveals where your energies lie in the problem-solving process. Every profile contributes in a meaningful way.

Please read the directions and answer all the questions. Allow about 10 minutes to complete the assessment.

Follow the link below to take FourSight online.
{{assessment_link}}

Deadline: Complete the assessment by {{deadline}}

Thank you!`,
};

/**
 * Default templates for the detail page's "Reminders & Messages" table. The participant
 * invitation defaults to the invitation's own `email`; per-invitation edits are stored
 * in `Invitation.messages`.
 */
export const MESSAGE_TEMPLATES: Record<Exclude<MessageKey, 'participant_invitation'>, EmailTemplate> = {
  participant_reminder: {
    subject: 'Reminder: complete your FourSight assessment',
    body:
`Dear Participant,

This is a friendly reminder to complete your FourSight Thinking Profile before the deadline. It only takes about 10 minutes.

Follow the link below to finish:
{{assessment_link}}

Deadline: {{deadline}}

Thank you!`,
  },
  thinking_tips: {
    subject: 'Thinking tips: get the most from your FourSight results',
    body:
`Dear Participant,

Now that you've seen your FourSight Thinking Profile, here are a few tips to put it to work:

- Notice where your energy peaks (Clarify, Ideate, Develop or Implement) and lean into it on your next challenge.
- Watch for the low-energy stages — that's where teaming up with a different thinking preference pays off.
- Share your profile with your team so you can plan around each other's strengths.

We'll explore these together at the presentation on {{presentation}}.

Thank you!`,
  },
};

/** Follow-up reminder emails shown in the wizard's "preview" popup. */
export const FOLLOWUP_EMAILS = [
  {
    name: 'Participant reminder',
    subject: 'Reminder: complete your FourSight assessment',
    body:
`Dear Participant,

This is a friendly reminder to complete your FourSight Thinking Profile before the deadline. It only takes about 10 minutes.

Follow the link below to finish:
{{assessment_link}}

Deadline: {{deadline}}

Thank you!`,
  },
  {
    name: 'Final reminder',
    subject: 'Last chance: your FourSight assessment closes soon',
    body:
`Dear Participant,

Your FourSight assessment closes soon and we don't yet have your results. Please take a few minutes to complete it before the deadline so you can take part in the session.

{{assessment_link}}

Deadline: {{deadline}}

Thank you!`,
  },
];

function sched(deadline: string, presentation: string, followup?: string) {
  const steps = [
    { id: 's1', step: 1, name: 'deadline', eventName: 'Participant deadline', date: deadline },
    { id: 's2', step: 2, name: 'presentation', eventName: 'Presentation', date: presentation },
  ];
  if (followup) { steps.push({ id: 's3', step: 3, name: 'followup', eventName: 'First follow-up email', date: followup }); }
  return steps;
}

const org = (id: string, name: string, role: OrganizerRole = 'owner', notify = false, accepted = true) => ({
  id, name, role, notify, accepted, email: name.toLowerCase().replace(/[^a-z]+/g, '.') + '@example.com',
});
const PROFILE_IDS: Record<string, number> = {
  Clarifier: 1, Ideator: 2, Developer: 3, Implementer: 4, 'Early Bird': 5, Analyst: 6,
  Accelerator: 7, Theorist: 8, Driver: 9, Finisher: 10, Hare: 11, 'Idea Broker': 12,
  Optimist: 13, Integrator: 14, Realist: 15,
};

/**
 * Scores are DERIVED from the profile type: peaks land high, the rest low. They used to come
 * from `(fn + ln).length`, independent of profileType — which seeded people who cannot exist
 * (a Developer scoring Implement-dominant). The profile type IS the peak set, so the two must
 * agree: the board's preference chip and the app-fs-icon on the same row render the same fact.
 *
 * `s` still varies the numbers within their band so the seed doesn't look rubber-stamped, and
 * stays deterministic (no Date.now(), no randomness).
 */
const part = (id: string, fn: string, ln: string, state: ParticipantState, profileType?: string, unlocked = true) => {
  const s = (fn + ln).length;
  const profileTypeId = profileType ? PROFILE_IDS[profileType] : undefined;
  const peaks = peaksFor(profileTypeId);
  const score = (p: Preference, i: number) =>
    peaks.includes(p)
      ? 30 + ((s * (i + 3)) % 9)   // 30–38: high
      : 14 + ((s * (i + 2)) % 8);  // 14–21: low
  return {
    id, firstName: fn, lastName: ln, state, profileType, profileTypeId,
    email: (fn + '.' + ln).toLowerCase() + '@example.com',
    answered: state === 'started' ? 1 + (s % 9) : undefined,
    lastActivity: state === 'not_started' ? undefined : '2026-05-10T00:00:00Z',
    scores: state === 'scored'
      ? { cl: score('cl', 0), id: score('id', 1), dv: score('dv', 2), im: score('im', 3) }
      : undefined,
    unlocked: state === 'scored' ? unlocked : undefined,
  };
};

// A large participant roster to exercise the participants grid (pagination + locked profiles)
const BIG_NAMES: [string, string][] = [
  ['Alice', 'Anderson'], ['Bob', 'Brennan'], ['Carol', 'Chen'], ['David', 'Diaz'],
  ['Emma', 'Evans'], ['Frank', 'Foster'], ['Grace', 'Gupta'], ['Henry', 'Hughes'],
  ['Ivy', 'Ibrahim'], ['Jack', 'Jensen'], ['Kara', 'Klein'], ['Liam', 'Lopez'],
  ['Mona', 'Mehta'], ['Noah', 'Novak'], ['Olivia', 'Owens'], ['Paul', 'Park'],
];
const BIG_PROFILES = ['Clarifier', 'Ideator', 'Developer', 'Implementer', 'Integrator'];
const bigParts = (): ReturnType<typeof part>[] => BIG_NAMES.map((n, i) => {
  const state: ParticipantState = i < 11 ? 'scored' : i < 14 ? 'started' : 'not_started';
  // First 7 scored are unlocked; bp8–bp11 (Henry, Ivy, Jack, Kara) stay locked to demo unlocking.
  const unlocked = i < 7;
  return part('bp' + (i + 1), n[0], n[1], state, state === 'scored' ? BIG_PROFILES[i % 5] : undefined, unlocked);
});

const rname = (id: string) => ROSTERS_SEED.find(r => r.id === id)!.name;

export const INVITATIONS_SEED: Invitation[] = [
  {
    id: 'inv-company-summit', name: 'Company-Wide Annual Strategy Summit & Team Effectiveness Program',
    rosterId: 'r2', rosterName: rname('r2'), language: 'English', status: 'open', estimatedParticipants: 16,
    whoInvites: 'i_email', whoPays: 'i_pay', howResults: 'i_present',
    schedule: sched('2026-09-30T00:00:00Z', '2026-10-07T00:00:00Z', '2026-09-22T00:00:00Z'),
    organizers: [org('o0a', 'Ada King', 'owner', true), org('o0b', 'Tony Stark', 'presenter', false, false)],
    // bigParts() alone yields only 7 eligible (scored + unlocked), which Similar split 5/1/1 with
    // no Develop group. The extra roster below spans all four preferences so the session board
    // (Task 4+) has enough eligible participants to fill a four-column split.
    participants: [
      ...bigParts(),
      part('p-40', 'Amara', 'Osei', 'scored', 'Clarifier'),
      part('p-41', 'Ben', 'Whitlock', 'scored', 'Ideator'),
      part('p-42', 'Chen', 'Wei', 'scored', 'Developer'),
      part('p-43', 'Dana', 'Reyes', 'scored', 'Implementer'),
      part('p-44', 'Eli', 'Novak', 'scored', 'Early Bird'),
      part('p-45', 'Farah', 'Khan', 'scored', 'Analyst'),
      part('p-46', 'Gus', 'Lindqvist', 'scored', 'Accelerator'),
      part('p-47', 'Hana', 'Suzuki', 'scored', 'Theorist'),
      part('p-48', 'Ivan', 'Petrov', 'scored', 'Driver'),
      part('p-49', 'Jae-won', 'Park', 'scored', 'Finisher'),
      part('p-50', 'Kofi', 'Mensah', 'scored', 'Hare'),
      part('p-51', 'Lena', 'Brandt', 'scored', 'Idea Broker'),
      part('p-52', 'Marco', 'Ruiz', 'scored', 'Optimist'),
      part('p-53', 'Nadia', 'Halim', 'scored', 'Integrator'),
      part('p-54', 'Omar', 'Farouk', 'scored', 'Realist'),
      part('p-55', 'Priya', 'Nair', 'scored', 'Clarifier'),
    ],
    // Spendable balance of 2 against 4 locked profiles: unlocking ≤2 succeeds, all 4 shows the shortfall path.
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 2, needed: 2 },
    followupEmailsEnabled: true, archived: false, createdAt: '2026-06-01T00:00:00Z', updatedAt: '2026-06-24T00:00:00Z',
  },
  {
    id: 'inv-q3-leadership', name: 'Q3 2026 Senior Leadership Development & Strategic Alignment Cohort (Group A)',
    rosterId: 'r1', rosterName: rname('r1'), language: 'English', status: 'open', estimatedParticipants: 10,
    whoInvites: 'i_email', whoPays: 'i_pay', howResults: 'i_present',
    schedule: sched('2026-07-09T00:00:00Z', '2026-07-09T00:00:00Z'),
    organizers: [org('o1', 'Test Owner'), org('o2', 'Ashish Kumar', 'presenter', true)],
    participants: [
      part('p1', 'Mike', 'Cool', 'scored', 'Developer'),
      part('p2', 'Harry', 'Datson', 'started'),
      part('p3', 'Jane', 'Roe', 'not_started'),
    ],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 6, needed: 4 },
    followupEmailsEnabled: false, archived: false, createdAt: '2026-05-01T00:00:00Z', updatedAt: '2026-05-01T00:00:00Z',
  },
  {
    id: 'inv-design-workshop', name: 'Global Product Design & Innovation Team Collaboration Workshop',
    rosterId: 'r2', rosterName: rname('r2'), language: 'English', status: 'open', estimatedParticipants: 25,
    whoInvites: 'i_email', whoPays: 'i_pay', howResults: 'i_present',
    schedule: sched('2026-08-01T00:00:00Z', '2026-08-05T00:00:00Z', '2026-07-25T00:00:00Z'),
    organizers: [org('o3', 'Ada King')], participants: [],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 40, needed: 0 },
    followupEmailsEnabled: true, archived: false, createdAt: '2026-05-10T00:00:00Z', updatedAt: '2026-05-12T00:00:00Z',
  },
  {
    id: 'inv-sales-kickoff', name: 'Annual Sales Kickoff & Revenue Enablement Conference 2026',
    rosterId: 'r3', rosterName: rname('r3'), language: 'English', status: 'closed', estimatedParticipants: 34,
    whoInvites: 'group_coordinator', whoPays: 'participant_pays', howResults: 'instant_profile',
    schedule: sched('2026-05-02T00:00:00Z', '2026-05-02T00:00:00Z'),
    organizers: [org('o4', 'Bill Lumbergh')], participants: [part('p4', 'Cde', 'Mail', 'scored', 'Integrator')],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 0, needed: 0 },
    followupEmailsEnabled: false, archived: false, createdAt: '2026-04-01T00:00:00Z', updatedAt: '2026-05-02T00:00:00Z',
  },
  {
    id: 'inv-onboarding', name: 'New Hire Onboarding & Cultural Immersion Program',
    rosterId: 'r1', rosterName: rname('r1'), language: 'English', status: 'open', estimatedParticipants: 5,
    whoInvites: 'i_email', whoPays: 'i_pay', howResults: 'i_present',
    schedule: sched('2026-09-01T00:00:00Z', '2026-09-10T00:00:00Z'),
    organizers: [org('o5', 'Test Owner')], participants: [],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 6, needed: 0 },
    followupEmailsEnabled: false, archived: false, createdAt: '2026-06-15T00:00:00Z', updatedAt: '2026-06-15T00:00:00Z',
  },
  {
    id: 'inv-archived-old', name: 'Legacy 2025 Pilot Group — Archived Reference Cohort',
    rosterId: 'r2', rosterName: rname('r2'), language: 'English', status: 'closed', estimatedParticipants: 12,
    whoInvites: 'i_email', whoPays: 'i_pay', howResults: 'i_present',
    schedule: sched('2025-12-01T00:00:00Z', '2025-12-05T00:00:00Z'),
    organizers: [org('o6', 'Ada King')], participants: [],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 12, needed: 0 },
    followupEmailsEnabled: false, archived: true, createdAt: '2025-11-01T00:00:00Z', updatedAt: '2025-12-05T00:00:00Z',
  },
  {
    id: 'inv-exec-retreat', name: 'Executive Leadership Offsite Retreat & Team Effectiveness Intensive',
    rosterId: 'r4', rosterName: rname('r4'), language: 'English', status: 'open', estimatedParticipants: 14,
    whoInvites: 'group_coordinator', whoPays: 'i_pay', howResults: 'i_present',
    schedule: sched('2026-08-20T00:00:00Z', '2026-08-28T00:00:00Z', '2026-08-12T00:00:00Z'),
    organizers: [org('o7', 'Diana Prince'), org('o8', 'Bruce Wayne', 'presenter', false, false)],
    participants: [
      part('p5', 'Clark', 'Kent', 'scored', 'Clarifier'), part('p6', 'Lois', 'Lane', 'started'),
      part('xr1', 'Ingrid', 'Solberg', 'scored', 'Ideator'),
      part('xr2', 'Desmond', 'Achebe', 'scored', 'Developer'),
      part('xr3', 'Priya', 'Raghavan', 'scored', 'Early Bird'),
      part('xr4', 'Emmett', 'Kowalski', 'scored', 'Theorist'),
      part('xr5', 'Nadia', 'Farouk', 'scored', 'Integrator'),
      part('xr6', 'Sullivan', 'Reyes', 'scored', 'Hare'),
      part('xr7', 'Beatrix', 'Lindqvist', 'scored', 'Driver'),
      part('xr8', 'Oscar', 'Mbeki', 'scored', 'Implementer', false), // locked — proves the pool excludes locked profiles
    ],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 25, needed: 0 },
    followupEmailsEnabled: true, archived: false, createdAt: '2026-06-01T00:00:00Z', updatedAt: '2026-06-18T00:00:00Z',
  },
  {
    id: 'inv-eng-allhands', name: 'Engineering All-Hands Creative Problem-Solving & Innovation Session',
    rosterId: 'r2', rosterName: rname('r2'), language: 'English', status: 'open', estimatedParticipants: 48,
    whoInvites: 'i_email', whoPays: 'i_pay', howResults: 'instant_profile_slideshow',
    schedule: sched('2026-09-15T00:00:00Z', '2026-09-22T00:00:00Z'),
    organizers: [org('o9', 'Ada King')], participants: [],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 40, needed: 8 },
    followupEmailsEnabled: false, archived: false, createdAt: '2026-06-05T00:00:00Z', updatedAt: '2026-06-20T00:00:00Z',
  },
  {
    id: 'inv-marketing-sprint', name: 'Marketing Department Quarterly Planning & Creative Sprint',
    rosterId: 'r5', rosterName: rname('r5'), language: 'English', status: 'open', estimatedParticipants: 9,
    whoInvites: 'i_email', whoPays: 'participant_pays', howResults: 'instant_profile',
    schedule: sched('2026-07-30T00:00:00Z', '2026-08-04T00:00:00Z'),
    organizers: [org('o10', 'Peggy Olson', 'presenter')],
    participants: [part('p7', 'Don', 'Draper', 'scored', 'Ideator'), part('p8', 'Joan', 'Holloway', 'scored', 'Implementer'), part('p9', 'Pete', 'Campbell', 'not_started')],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 12, needed: 0 },
    followupEmailsEnabled: false, archived: false, createdAt: '2026-06-10T00:00:00Z', updatedAt: '2026-06-22T00:00:00Z',
  },
  {
    id: 'inv-ops-review', name: 'Operations & Supply Chain Continuous Improvement Review',
    rosterId: 'r4', rosterName: rname('r4'), language: 'English', status: 'open', estimatedParticipants: 20,
    whoInvites: 'i_email', whoPays: 'i_pay', howResults: 'i_present',
    schedule: sched('2026-10-01T00:00:00Z', '2026-10-09T00:00:00Z'),
    organizers: [org('o11', 'Tony Stark')], participants: [],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 25, needed: 0 },
    followupEmailsEnabled: false, archived: false, createdAt: '2026-06-12T00:00:00Z', updatedAt: '2026-06-12T00:00:00Z',
  },
  {
    id: 'inv-hr-development', name: 'Human Resources Talent Development & Succession Planning Program',
    rosterId: 'r6', rosterName: rname('r6'), language: 'English', status: 'open', estimatedParticipants: 16,
    whoInvites: 'group_coordinator', whoPays: 'i_pay', howResults: 'i_present',
    schedule: sched('2026-08-08T00:00:00Z', '2026-08-15T00:00:00Z', '2026-08-01T00:00:00Z'),
    organizers: [org('o12', 'Pepper Potts', 'owner', true)],
    participants: [part('p10', 'Happy', 'Hogan', 'started'), part('p11', 'May', 'Parker', 'scored', 'Developer')],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 8, needed: 8 },
    followupEmailsEnabled: true, archived: false, createdAt: '2026-06-08T00:00:00Z', updatedAt: '2026-06-21T00:00:00Z',
  },
  {
    id: 'inv-finance-team', name: 'Finance & Accounting Team Collaboration Assessment',
    rosterId: 'r3', rosterName: rname('r3'), language: 'English', status: 'closed', estimatedParticipants: 7,
    whoInvites: 'i_email', whoPays: 'i_pay', howResults: 'i_present',
    schedule: sched('2026-04-15T00:00:00Z', '2026-04-20T00:00:00Z'),
    organizers: [org('o13', 'Bill Lumbergh')], participants: [part('p12', 'Milton', 'Waddams', 'scored', 'Clarifier')],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 0, needed: 0 },
    followupEmailsEnabled: false, archived: false, createdAt: '2026-03-20T00:00:00Z', updatedAt: '2026-04-20T00:00:00Z',
  },
  {
    id: 'inv-product-discovery', name: 'Product Discovery & Customer Insight Alignment Workshop',
    rosterId: 'r5', rosterName: rname('r5'), language: 'English', status: 'open', estimatedParticipants: 11,
    whoInvites: 'i_email', whoPays: 'i_pay', howResults: 'instant_profile',
    schedule: sched('2026-09-05T00:00:00Z', '2026-09-12T00:00:00Z'),
    organizers: [org('o14', 'Peggy Olson')],
    participants: [
      part('pd1', 'Harriet', 'Vance', 'scored', 'Ideator'),
      part('pd2', 'Julian', 'Okoro', 'scored', 'Clarifier'),
      part('pd3', 'Sofia', 'Restrepo', 'scored', 'Idea Broker'),
      part('pd4', 'Anders', 'Holm', 'scored', 'Developer'),
      part('pd5', 'Meera', 'Krishnan', 'scored', 'Optimist'),
      part('pd6', 'Gideon', 'Marsh', 'scored', 'Finisher'),
      part('pd7', 'Talia', 'Bergeron', 'scored', 'Early Bird'),
    ],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 12, needed: 0 },
    followupEmailsEnabled: false, archived: false, createdAt: '2026-06-14T00:00:00Z', updatedAt: '2026-06-19T00:00:00Z',
  },
  {
    id: 'inv-customer-success', name: 'Customer Success Organization Cross-Functional Cohort',
    rosterId: 'r6', rosterName: rname('r6'), language: 'English', status: 'open', estimatedParticipants: 18,
    whoInvites: 'i_email', whoPays: 'i_pay', howResults: 'i_present',
    schedule: sched('2026-08-25T00:00:00Z', '2026-09-01T00:00:00Z'),
    organizers: [org('o15', 'Tony Stark'), org('o16', 'Pepper Potts', 'presenter')],
    participants: [
      part('p13', 'Phil', 'Coulson', 'scored', 'Integrator'), part('p14', 'Nick', 'Fury', 'started'),
      part('p15', 'Maria', 'Hill', 'not_started'),
      part('cs1', 'Renata', 'Duarte', 'scored', 'Accelerator'),
      part('cs2', 'Callum', 'Fitzgerald', 'scored', 'Developer'),
      part('cs3', 'Aiko', 'Tanaka', 'scored', 'Ideator'),
      part('cs4', 'Bertrand', 'Nkemdirim', 'scored', 'Realist'),
      part('cs5', 'Simone', 'Ferrari', 'scored', 'Theorist'),
      part('cs6', 'Wesley', 'Amadi', 'scored', 'Clarifier'),
      part('cs7', 'Halima', 'Yusuf', 'scored', 'Driver'),
    ],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 8, needed: 10 },
    followupEmailsEnabled: false, archived: false, createdAt: '2026-06-16T00:00:00Z', updatedAt: '2026-06-23T00:00:00Z',
  },
  {
    id: 'inv-data-science', name: 'Data Science & Analytics Center of Excellence Kickoff',
    rosterId: 'r2', rosterName: rname('r2'), language: 'English', status: 'open', estimatedParticipants: 13,
    whoInvites: 'i_email', whoPays: 'i_pay', howResults: 'instant_profile_slideshow',
    schedule: sched('2026-07-22T00:00:00Z', '2026-07-29T00:00:00Z'),
    organizers: [org('o17', 'Ada King', 'owner', true)],
    participants: [part('p16', 'Alan', 'Turing', 'scored', 'Developer')],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 40, needed: 0 },
    followupEmailsEnabled: false, archived: false, createdAt: '2026-06-02T00:00:00Z', updatedAt: '2026-06-17T00:00:00Z',
  },
  {
    id: 'inv-board-prep', name: 'Board of Directors Strategic Offsite Preparation Session',
    rosterId: 'r1', rosterName: rname('r1'), language: 'English', status: 'open', estimatedParticipants: 6,
    whoInvites: 'group_coordinator', whoPays: 'i_pay', howResults: 'i_present',
    schedule: sched('2026-10-10T00:00:00Z', '2026-10-18T00:00:00Z'),
    organizers: [org('o18', 'Test Owner')],
    // A board: heavy on Implement, with nobody peaking in Ideate. Deliberate — it gives the
    // report picker's composition rail a real thin stage to warn about.
    participants: [
      part('bd1', 'Marcus', 'Webb', 'scored', 'Implementer'),
      part('bd2', 'Rosalind', 'Fry', 'scored', 'Accelerator'),
      part('bd3', 'Tobias', 'Nunez', 'scored', 'Finisher'),
      part('bd4', 'Yvonne', 'Baptiste', 'scored', 'Realist'),
      part('bd5', 'Cyrus', 'Hale', 'scored', 'Analyst'),
      part('bd6', 'Delia', 'Marchetti', 'scored', 'Implementer'),
    ],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 6, needed: 0 },
    followupEmailsEnabled: false, archived: false, createdAt: '2026-06-13T00:00:00Z', updatedAt: '2026-06-20T00:00:00Z',
  },
  {
    id: 'inv-it-transformation', name: 'IT Digital Transformation & Change Management Program',
    rosterId: 'r4', rosterName: rname('r4'), language: 'English', status: 'open', estimatedParticipants: 22,
    whoInvites: 'i_email', whoPays: 'i_pay', howResults: 'i_present',
    schedule: sched('2026-11-01T00:00:00Z', '2026-11-12T00:00:00Z'),
    organizers: [org('o19', 'Bruce Wayne')], participants: [],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 25, needed: 0 },
    followupEmailsEnabled: false, archived: false, createdAt: '2026-06-11T00:00:00Z', updatedAt: '2026-06-11T00:00:00Z',
  },
  {
    id: 'inv-legacy-2024', name: 'Archived 2024 Regional Managers Pilot Cohort',
    rosterId: 'r3', rosterName: rname('r3'), language: 'English', status: 'closed', estimatedParticipants: 15,
    whoInvites: 'i_email', whoPays: 'i_pay', howResults: 'i_present',
    schedule: sched('2024-11-01T00:00:00Z', '2024-11-08T00:00:00Z'),
    organizers: [org('o20', 'Bill Lumbergh')], participants: [],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 0, needed: 0 },
    followupEmailsEnabled: false, archived: true, createdAt: '2024-10-01T00:00:00Z', updatedAt: '2024-11-08T00:00:00Z',
  },
  {
    id: 'inv-startup-cohort', name: 'Startup Founders Creative Problem-Solving Accelerator Cohort',
    rosterId: 'r5', rosterName: rname('r5'), language: 'English', status: 'open', estimatedParticipants: 8,
    whoInvites: 'i_email', whoPays: 'participant_pays', howResults: 'instant_profile',
    schedule: sched('2026-08-18T00:00:00Z', '2026-08-22T00:00:00Z', '2026-08-10T00:00:00Z'),
    organizers: [org('o21', 'Peggy Olson', 'owner', true)],
    participants: [part('p17', 'Walter', 'White', 'scored', 'Ideator'), part('p18', 'Jesse', 'Pinkman', 'started')],
    email: { ...DEFAULT_EMAIL }, scoringKeys: { available: 12, needed: 0 },
    followupEmailsEnabled: true, archived: false, createdAt: '2026-06-09T00:00:00Z', updatedAt: '2026-06-24T00:00:00Z',
  },
];
